import { useCallback } from 'react'
import { useShowAppError } from '@/services/appErrors/appErrorAtom'
import {
  type CartEntriesAddMutation,
  ProductRecommendationsDocument,
  ShopSessionDocument,
  useCartEntriesAddMutation,
  useCartEntryReplaceMutation,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import type { ShopSession } from '@/services/shopSession/ShopSession.types'

type Params = {
  shopSessionId: string
  onSuccess: (productOfferId: string, data: ShopSession['cart']) => void
  onError?: (error: Error) => void
  entryToReplace?: string
}

export const useAddToCart = (params: Params) => {
  const options = {
    // An alterntive approach to make sure we don't display stale data is to reset the entire
    // Apollo cache https://www.apollographql.com/docs/react/caching/advanced-topics#resetting-the-cache
    refetchQueries: [
      ProductRecommendationsDocument,
      { query: ShopSessionDocument, variables: { shopSessionId: params.shopSessionId } },
    ],
    awaitRefetchQueries: true,
  }

  const [addEntries, { loading }] = useCartEntriesAddMutation(options)
  const [replaceEntry, { loading: loadingReplace }] = useCartEntryReplaceMutation(options)

  const showError = useShowAppError()
  const addToCart = useCallback(
    async (productOfferIds: string | Array<string>) => {
      const ids = Array.isArray(productOfferIds) ? productOfferIds : [productOfferIds]

      const handleError = (error: Error) => {
        params.onError?.(error)
        showError(error)
      }

      if (params.entryToReplace) {
        // Replace the existing entry with the first id, then add the remaining recommended offer (if any)
        const [firstId, ...rest] = ids

        if (rest.length > 0) {
          // Handle replace operation first - don't call onSuccess yet
          const replaceResult = await replaceEntry({
            variables: {
              shopSessionId: params.shopSessionId,
              addOfferId: firstId,
              removeOfferId: params.entryToReplace,
            },
            onError: handleError,
          })

          // Bail out if replace failed - don't add cross-sell to cart
          if (!replaceResult.data) {
            return
          }

          // Handle additional entries - call onSuccess only after this completes
          await addEntries({
            variables: {
              shopSessionId: params.shopSessionId,
              offerIds: rest,
            },
            onCompleted: (data: CartEntriesAddMutation) => {
              const updatedShopSession = data.shopSessionCartEntriesAdd.shopSession
              if (!updatedShopSession) return

              const addedOffer = updatedShopSession.cart.items.find((item) =>
                ids.includes(item.productOffer.id),
              )?.productOffer

              if (!addedOffer) {
                logger.error('Added offer missing in cart, this should not happen', {
                  shopSessionId: params.shopSessionId,
                  productOfferIds: ids,
                })
              }

              // Report success using the first added id for backwards compatibility
              params.onSuccess(ids[0], updatedShopSession.cart)
            },
            onError: handleError,
          })
        } else {
          // No additional entries, call onSuccess after replace completes
          // The replace mutation also has shopSessionCartEntriesAdd in its response
          await replaceEntry({
            variables: {
              shopSessionId: params.shopSessionId,
              addOfferId: firstId,
              removeOfferId: params.entryToReplace,
            },
            onCompleted: (data) => {
              // The replace mutation returns shopSessionCartEntriesAdd in its response (same structure as CartEntriesAddMutation)
              const replaceData = data as {
                shopSessionCartEntriesAdd?: CartEntriesAddMutation['shopSessionCartEntriesAdd']
              }
              const updatedShopSession = replaceData.shopSessionCartEntriesAdd?.shopSession
              if (!updatedShopSession) return

              const addedOffer = updatedShopSession.cart.items.find((item) =>
                ids.includes(item.productOffer.id),
              )?.productOffer

              if (!addedOffer) {
                logger.error('Added offer missing in cart, this should not happen', {
                  shopSessionId: params.shopSessionId,
                  productOfferIds: ids,
                })
              }

              params.onSuccess(ids[0], updatedShopSession.cart)
            },
            onError: handleError,
          })
        }
      } else {
        // No replace, just add entries
        await addEntries({
          variables: {
            shopSessionId: params.shopSessionId,
            offerIds: ids,
          },
          onCompleted: (data: CartEntriesAddMutation) => {
            const updatedShopSession = data.shopSessionCartEntriesAdd.shopSession
            if (!updatedShopSession) return

            const addedOffer = updatedShopSession.cart.items.find((item) =>
              ids.includes(item.productOffer.id),
            )?.productOffer

            if (!addedOffer) {
              logger.error('Added offer missing in cart, this should not happen', {
                shopSessionId: params.shopSessionId,
                productOfferIds: ids,
              })
            }

            // Report success using the first added id for backwards compatibility
            params.onSuccess(ids[0], updatedShopSession.cart)
          },
          onError: handleError,
        })
      }
    },
    [addEntries, replaceEntry, showError, params],
  )

  return [addToCart, loadingReplace || loading] as const
}
